<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-3 items-center flex-shrink-0 sm:pr-10">
        <div class="flex-grow flex items-center">
          <div class="text-13 font-600">{{ $t('projects.templates.title') }}</div>
        </div>
        <div class="ml-auto flex flex-row justify-center">
          <PillButton :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
      </div>
      <div class="w-full flex-grow overflow-hidden pb-2 flex flex-col">
        <template v-if="templates.length">
          <TableElevated :items="templates" :headers="templateHeaders" class="text-13 pr-3 sm:pr-10 min-h-300">
            <template v-slot:[`item.name`]="{ value }">
              <span class="text-13 font-500 truncate">{{ value }}</span>
            </template>
            <template v-slot:[`item.tags`]="{ item }">
              <Badge v-for="tag in item.tags" :key="tag" :text="tag" :error="tag === 'readonly'" :info="tag === 'system'" :success="tag === 'user'" small class="ml-1 px-1 uppercase"></Badge>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <IconButton icon="edit" class="mr-1 text-primary" @click="onEditClicked(item)" />
              <IconButton :disabled="item.tags && item.tags.includes('system')" class="text-primary" icon="delete" @click="onDeleteClicked(item)" />
            </template>
          </TableElevated>
          <div class="flex-shrink-0 py-4 px-2 mt-2 border-t border-gray-300">
            <div class="mb-2 text-12 text-dark-text">Use a template</div>
            <div class="flex pb-3 flex-wrap -ml-3">
              <div v-for="(template, index) in predefinedTemplates" :key="index" class="w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5 pl-2 pt-3 pr-1">
                <div
                  class="
                    select-none
                    cursor-pointer
                    shadow-mini-card-alt
                    rounded-14
                    flex flex-col
                    p-4
                    gap-2
                    h-full
                    justify-start
                    text-12
                    rounded-br-none
                    border border-solid border-secondary border-opacity-10
                    text-semi-dark-text text-opacity-80
                    flex-shrink-0
                  "
                >
                  <div class="flex items-center text-primary">
                    <Icon :name="template.icon" class="w-5 h-5 mr-2 flex-shrink-0" />
                    <span class="font-500">{{ template.label }}</span>
                  </div>
                  <div class="text-10">{{ template.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="flex flex-col h-full flex-grow items-center justify-center pb-50">
          <div class="my-2 text-22 text-gray-700">{{ $t('projects.templates.have_no_templates') }}</div>
          <div class="my-2 text-gray-600">{{ $t('projects.templates.create_first_template') }}</div>
          <PillButton class="my-3" :text="$t('projects.templates.create_new')" primary @click="onCreateClicked" />
        </div>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import DeleteModal from '../../modals/DeleteModal.vue';
import CreateTemplateModal from '../../project/CreateTemplateModal';

export default {
  name: 'EmailTemplates',
  data() {
    return {
      fetching: false,
      predefinedTemplates: [
        {
          label: 'Welcome Email',
          description: 'First-touch email to welcome new customers, users, or partners.',
          icon: 'mark_email_read',
        },
        {
          label: 'Meeting Agenda',
          description: 'Set clear expectations for upcoming meetings.',
          icon: 'event_note',
        },
        {
          label: 'Project Update Report',
          description: 'Keep stakeholders informed on project progress.',
          icon: 'bar_chart',
        },
        {
          label: 'Follow-Up Email',
          description: 'After meetings, proposals, or sales calls.',
          icon: 'forward_to_inbox',
        },
        {
          label: 'Invoice',
          description: 'Standardized billing communication.',
          icon: 'request_quote',
        },
        {
          label: 'Customer Feedback Request',
          description: 'Ask customers for feedback after purchase or interaction.',
          icon: 'rate_review',
        },
        {
          label: 'Service Proposal Document',
          description: 'Propose services to clients.',
          icon: 'description',
        },
        {
          label: 'Employee Onboarding Checklist',
          description: 'Help HR teams onboard new employees smoothly.',
          icon: 'checklist',
        },
        {
          label: 'Legal/Compliance Notice',
          description: 'Communicate policy updates or compliance notices.',
          icon: 'gavel',
        },
        {
          label: 'Performance Review Document',
          description: 'Standardized performance review for HR or managers.',
          icon: 'trending_up',
        },
      ],
    };
  },
  computed: {
    ...mapState(['projectData']),
    projectId() {
      return this.$route.params.projectId;
    },
    templates() {
      const templates = this.projectData?.[this.projectId]?.templates || {};
      return Object.values(templates);
    },
    templateHeaders() {
      return [
        { key: 'name', title: this.$t('name') },
        { key: 'tags', title: this.$t('tags'), width: '140px' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
  },
  methods: {
    ...mapActions(['fetchProjectTemplates', 'showToastMessage', 'deleteProjectTemplate']),
    onCreateClicked() {
      this.$showModal(CreateTemplateModal, { projectId: this.projectId });
    },
    onEditClicked(template) {
      this.$showModal(CreateTemplateModal, { projectId: this.projectId, templateId: template.template_id });
    },
    onDeleteClicked(emailTemplate) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.email_template.remove_template', { templateName: emailTemplate.name }),
        info: null,
        onConfirm: async () => {
          await this.onDeleteConfirmClicked(emailTemplate);
        },
      });
    },
    async onDeleteConfirmClicked(item) {
      const response = await this.deleteProjectTemplate({ project_id: this.projectId, template_id: item.template_id });
      if (response) {
        await this.fetchProjectTemplates({ projectId: this.projectId });
      }
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectTemplates({ projectId: this.$route.params.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
